import React, { Component } from "react";
import cns from "classnames";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  withStyles
} from "@material-ui/core";
import { ExpandMore, FolderOutlined } from "@material-ui/icons";

import { sortBy } from "lodash";
import { downloadFileById } from "@api";
import locale from "@bonliva-crm/locale";
import { Classes, ThemeType } from "@theme";

import { SpinnerOverlay } from "@components";
import Body from "@components/typography/Body";
import { Subheading } from "@components/typography/Subheading";
import DocumentEntry, { DocumentEntryProps } from "./DocumentEntry";
import { Document } from "./utils";

type Props<T extends Document> = {
  className?: string;
  classes: Classes<typeof styles>;
  documents: T[];
  emptyMessage?: React.ReactNode;
  groupBy?: (doc: T) => string;
  loading?: boolean;
  onDelete?: (doc: T) => any;
  hideDelete?: (doc: T) => boolean;
  disabledOnDocument?: (doc: T) => boolean;
  disabledOnDelete?: (doc: T) => boolean;
  disabledOnDeleteTooltip?: string;
  getRowProps?: (doc: T) => Partial<DocumentEntryProps>;
  hideEmptyTitle?: boolean;
  style?: React.CSSProperties;
  orderBy?: (docs: T[]) => T[];
};

export class DocumentList<T extends Document> extends Component<Props<T>> {
  static defaultProps: Partial<Props<any>> = {
    documents: [],
    emptyMessage: locale["No files found"],
    getRowProps: () => ({})
  };

  render() {
    const { className, classes, documents, loading, hideEmptyTitle } = this.props;
    const sorted = sortBy(documents, (x) => x.FileRef.Name); // array.sortBy crashes

    return (
      <div className={cns(classes.root, className)} style={this.props.style}>
        <SpinnerOverlay show={this.props.loading} />

        {!loading && sorted.length === 0 && !hideEmptyTitle && (
          <Body block className={classes.emptyTitle}>
            {this.props.emptyMessage}
          </Body>
        )}

        {this.props.groupBy && this.renderGrouped(documents)}
        {!this.props.groupBy && this.renderPlain(documents)}
      </div>
    );
  }

  private renderPlain = (docs: T[]) => {
    const { classes, orderBy } = this.props;

    if (!Array.isArray(docs) || docs.length === 0) {
      return null;
    }
    const items = orderBy ? orderBy(docs) : docs;

    return (
      <div className={classes.list}>
        {items.map((doc, idx) => {
          return (
            <DocumentEntry
              key={doc.FileRef.NodeID + idx}
              data={doc}
              onDownload={() => downloadFileById(doc.FileRef.NodeID, doc.FileRef.Name)}
              onOpen={() => window.open(`/shared-files/${doc.FileRef.NodeID}`, "_blank")}
              onDelete={this.props.onDelete ? () => this.props.onDelete(doc) : null}
              disabledOnDelete={
                this.props.disabledOnDelete ? this.props.disabledOnDelete(doc) : false
              }
              disableDocument={
                this.props.disabledOnDocument ? this.props.disabledOnDocument(doc) : false
              }
              hideDelete={this.props.hideDelete ? !!this.props.hideDelete(doc) : false}
              disabledOnDeleteTooltip={
                this.props.disabledOnDeleteTooltip ||
                "Det måste finnas minst ett dokument uppladdat på bokningen. Lägg till ett nytt dokument för att kunna ta bort detta dokumentet."
              }
              showFileIcon
              {...this.props.getRowProps(doc)}
            />
          );
        })}
      </div>
    );
  };

  private renderGrouped = (docs: T[]) => {
    const { classes } = this.props;
    const grouped = sortBy(docs.groupBy(this.props.groupBy), ["key"]);

    return grouped.map(({ key: group, values: documents }) => {
      return (
        <Accordion id={group} key={group} className={classes.expPanel}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Subheading className={classes.title}>
              <FolderOutlined />
              {group}
            </Subheading>
          </AccordionSummary>

          <AccordionDetails className={classes.expPanelDetails}>
            {this.renderPlain(documents)}
          </AccordionDetails>
        </Accordion>
      );
    });
  };
}

const styles = (theme: ThemeType) => ({
  root: { position: "relative", padding: 16 },
  noShadow: { boxShadow: "none" },
  expPanel: {
    boxShadow: "none",
    "&:first-child": {
      borderRadius: 0
    }
  },
  expPanelDetails: { paddingBottom: 8 },
  title: {
    display: "flex",
    "&  svg": { marginRight: 8 }
  },
  emptyTitle: { textAlign: "center" },

  list: {
    ...theme.mixins.childrenMarginBottom(theme.spacing()),
    width: "100%"
  }
});

export default withStyles(styles as any)(DocumentList);
